/**
 * Identifiers and tokens.
 *
 * Public references (IECN-260808-1042) are human-readable and safe to say over the phone.
 * They are NEVER the security boundary — tracking is gated by a high-entropy token whose
 * SHA-256 hash is what the database stores. See docs/SECURITY.md.
 */
const CRYPTO = globalThis.crypto;
if (!CRYPTO?.getRandomValues) {
    throw new Error('A Web Crypto implementation is required (Node 20+ or any modern browser). Refusing to fall back to Math.random for security tokens.');
}
/** Unambiguous alphabet — no 0/O/1/I/L to survive being read aloud or hand-written. */
const READABLE_ALPHABET = '23456789ABCDEFGHJKMNPQRSTUVWXYZ';
const URL_SAFE_ALPHABET = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789-_';
function randomFromAlphabet(length, alphabet) {
    const bytes = new Uint8Array(length);
    CRYPTO.getRandomValues(bytes);
    let out = '';
    for (let i = 0; i < length; i++) {
        out += alphabet[bytes[i] % alphabet.length];
    }
    return out;
}
/** Cryptographically strong URL-safe token. Default 32 chars ≈ 190 bits. */
export function secureToken(length = 32) {
    return randomFromAlphabet(length, URL_SAFE_ALPHABET);
}
/** Short code a human can read back — PIN-style. */
export function readableCode(length = 6) {
    return randomFromAlphabet(length, READABLE_ALPHABET);
}
export function uuid() {
    return CRYPTO.randomUUID();
}
/** Numeric PIN for POD verification. */
export function numericPin(length = 4) {
    const bytes = new Uint8Array(length);
    CRYPTO.getRandomValues(bytes);
    let out = '';
    for (let i = 0; i < length; i++)
        out += String(bytes[i] % 10);
    return out;
}
/** SHA-256 hex digest. Works in Node and the browser. */
export async function sha256Hex(value) {
    const data = new TextEncoder().encode(value);
    const digest = await CRYPTO.subtle.digest('SHA-256', data);
    return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, '0')).join('');
}
function yymmdd(date) {
    const yy = String(date.getUTCFullYear()).slice(-2);
    const mm = String(date.getUTCMonth() + 1).padStart(2, '0');
    const dd = String(date.getUTCDate()).padStart(2, '0');
    return `${yy}${mm}${dd}`;
}
/**
 * Public delivery reference: IECN-YYMMDD-NNNN.
 * `sequence` is the per-day counter allocated inside the creating transaction, so two
 * concurrent bookings can never receive the same reference.
 */
export function deliveryReference(sequence, date = new Date()) {
    return `IECN-${yymmdd(date)}-${String(sequence).padStart(4, '0')}`;
}
export function quoteReference(sequence, date = new Date()) {
    return `IECNQ-${yymmdd(date)}-${String(sequence).padStart(4, '0')}`;
}
export function invoiceReference(sequence, date = new Date()) {
    return `IECNINV-${yymmdd(date)}-${String(sequence).padStart(4, '0')}`;
}
const DELIVERY_REF_PATTERN = /^IECN-\d{6}-\d{4,}$/;
export function isDeliveryReference(value) {
    return DELIVERY_REF_PATTERN.test(value);
}
/**
 * Tracking tokens are long, random and revocable. The plaintext goes in the link that is
 * texted or emailed to the customer; only the hash is persisted.
 */
export function trackingToken() {
    return secureToken(40);
}
/** Idempotency keys generated client-side for retry-safe mutations. */
export function idempotencyKey() {
    return `idem_${secureToken(28)}`;
}
//# sourceMappingURL=ids.js.map